// src/pages/PromptStatus2.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Navbar4 from "../components/Navbar4";
import { getSecondPromptStatus } from "../services/LLMCalls";
import ProcessWarning from "../components/ProcessWarning";
import ProcessingTime from "../components/ProcessingTime";

// ---------------------------------------------------------------------------
//  VALID STATUSES FOR STEP-2 (as implemented in the backend)
// ---------------------------------------------------------------------------
const STATUS_FLOW = ["processing", "complete"];

export const isForwardProgress = (prev, next) => {
  if (next === "error") return true;
  if (!STATUS_FLOW.includes(prev) || !STATUS_FLOW.includes(next)) return true;
  return STATUS_FLOW.indexOf(next) >= STATUS_FLOW.indexOf(prev);
};

const PromptStatus2 = () => {
  // -------------------------------------------------------------------------
  //  ROUTER & LOCATION DATA --------------------------------------------------
  // -------------------------------------------------------------------------
  const location = useLocation();
  const navigate = useNavigate();

  const {
    projectId,
    promptId,
    isProcessing,
    error: initialError
  } = location.state || {};

  // -------------------------------------------------------------------------
  //  LOCAL REACT STATE -------------------------------------------------------
  // -------------------------------------------------------------------------
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(initialError || null);
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [startTimestamp] = useState(Date.now()); // Stable timestamp that won't change

  // Refs --------------------------------------------------------------------
  const attemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  const statusRef = useRef(status);
  const hasNavigatedRef = useRef(false); // Track if we've already navigated

  // Keep statusRef in sync so polling closure always has the latest value
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const MAX_ATTEMPTS = 30;

  // -------------------------------------------------------------------------
  //  NAVIGATION GUARDS -------------------------------------------------------
  // -------------------------------------------------------------------------
  const handleNavigate = useCallback((to, options) => {
    if (statusRef.current === "processing") {
      setShowModal(true);
      setPendingNavigation(() => () => navigate(to, options));
    } else {
      navigate(to, options);
    }
  }, [navigate]);

  const handleBack = useCallback(() => {
    handleNavigate("/create-view", { state: { projectId } });
  }, [handleNavigate, projectId]);

  // Modal helpers -----------------------------------------------------------
  const handleModalCancel = () => {
    setShowModal(false);
    setPendingNavigation(null);
  };

  const handleModalContinue = () => {
    setShowModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  // Warn before browser unload while processing ----------------------------
  useEffect(() => {
    if (status !== "processing") return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [status]);

  // -------------------------------------------------------------------------
  //  POLLING LOOP -----------------------------------------------------------
  // -------------------------------------------------------------------------
  useEffect(() => {
    isMountedRef.current = true;
    hasNavigatedRef.current = false; // Reset navigation flag

    if (!projectId) {
      setError("Missing project ID");
      return;
    }
    if (!promptId && !isProcessing) {
      setError("Invalid status check request");
      return;
    }

    let timerId;

    const pollStatus = async () => {
      if (!isMountedRef.current || hasNavigatedRef.current) return false;
      
      try {
        if (!promptId) return true;

        const result = await getSecondPromptStatus(promptId);
        
        // Check if component is still mounted and we haven't navigated yet
        if (!isMountedRef.current || hasNavigatedRef.current) return false;

        const newStatus = result.step2_status;

        if (newStatus === "error") {
          setError(result.error_message || "An error occurred during processing.");
          setStatus("error");
        } else if (newStatus === "complete") {
          // Set the flag to prevent multiple navigations
          hasNavigatedRef.current = true;
          
          // Navigate immediately to avoid UI flicker
          navigate("/view-details", {
            state: {
              projectId,
              viewId: result.view_id
            },
            replace: true
          });
          return false; // stop polling
        } else if (
          newStatus !== statusRef.current &&
          isForwardProgress(statusRef.current, newStatus)
        ) {
          setStatus(newStatus);
        }

        return true; // keep polling
      } catch (err) {
        if (!isMountedRef.current || hasNavigatedRef.current) return false;
        
        console.error("[PromptStatus2] Error checking status:", err);
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setError("Request timed out. Please try again.");
          return false;
        }
        return true; // try again
      }
    };

    const tick = () => {
      pollStatus().then((keepGoing) => {
        if (keepGoing && isMountedRef.current && !hasNavigatedRef.current) {
          timerId = setTimeout(tick, 2000);
        }
      });
    };

    tick();

    return () => {
      isMountedRef.current = false;
      clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, promptId, isProcessing, navigate]);

  // -------------------------------------------------------------------------
  //  RENDER HELPERS ---------------------------------------------------------
  // -------------------------------------------------------------------------
  const ErrorDisplay = () => (
    <div className="text-center p-4">
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
      {(location.state?.isSubscriptionError || (error && (
        error.includes("trial") ||
        error.includes("expired") ||
        error.includes("Purchase") ||
        error.includes("access pass")
      ))) && (
        <button
          onClick={() => navigate("/payment")}
          className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 mr-2"
        >
          Purchase Access Pass
        </button>
      )}
      <button
        onClick={() => navigate("/create-view")}
        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-700"
      >
        Back to Create View
      </button>
    </div>
  );

  const LoadingDisplay = () => (
    <div className="text-center p-4">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900 mx-auto mb-4" />
      <p className="text-xl mb-2">Processing your request…</p>
      <p className="text-lg text-gray-600 mb-2">This may take a few moments</p>
      
      {/* Stable ProcessingTime component with fixed props */}
      <ProcessingTime
        key="stable-processing-time"
        promptType="step2"
        status="processing"
        startTimestamp={startTimestamp}
      />
    </div>
  );

  // -------------------------------------------------------------------------
  //  MAIN JSX ---------------------------------------------------------------
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar4
        title="Processing"
        backPath="/create-view"
        projectId={projectId}
        onBack={handleBack}
      />

      <div className="flex-grow flex items-center justify-center">
        {error ? <ErrorDisplay /> : <LoadingDisplay />}
      </div>

      <ProcessWarning
        open={showModal}
        onCancel={handleModalCancel}
        onContinue={handleModalContinue}
      />
    </div>
  );
};

export default PromptStatus2;